// ============================================================================
// First-load splash controller.
//
// The markup and CSS live inline in index.html so the splash paints on the
// first frame — this module only drives and dismisses it.
//
// Two rules it has to respect:
//   1. Never keep someone waiting for a decoration. There is a hard ceiling;
//      if the app is ready sooner, the splash leaves sooner.
//   2. Never flash. A splash visible for 120ms is worse than none, so there is
//      also a floor.
// ============================================================================

const MIN_VISIBLE_MS = 900;    // below this it reads as a glitch
const MAX_VISIBLE_MS = 4500;   // hard ceiling: never trap someone behind it
const SESSION_KEY = 'dw_splash_seen';

const startedAt = performance.now();
let dismissed = false;
let progress = 0;
let creepTimer = null;

const el = () => document.getElementById('dw-loader');

/**
 * Skip the splash for the rest of the session.
 *
 * It is a first-impression piece. Replaying it on every internal reload turns
 * a nice moment into a tax on the people who use this daily — creators
 * checking sales, admins working a review queue.
 */
export function shouldShowSplash() {
  try {
    return !sessionStorage.getItem(SESSION_KEY);
  } catch {
    return true;   // private mode: show it, no worse than the alternative
  }
}

/** Called before anything else, so a repeat visit never sees a flash. */
export function initLoader() {
  const node = el();
  if (!node) return;

  if (!shouldShowSplash()) {
    node.remove();
    dismissed = true;
    return;
  }

  startCreep();

  // Safety net: if boot hangs — a slow network, a failing query — the splash
  // must not become a permanent wall. Something visible beats a green screen.
  setTimeout(() => hideLoader(true), MAX_VISIBLE_MS);
}

/**
 * Report boot progress, 0..1.
 *
 * Everything reads from one custom property: liquid height, logo buoyancy,
 * the glow, and the bar all derive from --fill in CSS, so they physically
 * cannot drift out of sync with each other.
 *
 * Monotonic — progress never visibly goes backwards.
 */
export function setProgress(value) {
  progress = Math.max(progress, Math.min(1, value));
  el()?.style.setProperty('--fill', progress.toFixed(3));
}

/**
 * Creep the fill forward while we wait.
 *
 * A bar that sits still reads as broken. This eases toward a ceiling it never
 * reaches, so there is always motion, and the real completion is what closes
 * the last stretch.
 */
function startCreep() {
  const tick = () => {
    if (dismissed) return;
    if (progress < 0.9) {
      setProgress(progress + (0.9 - progress) * 0.06);
    }
    creepTimer = setTimeout(tick, 90);
  };
  creepTimer = setTimeout(tick, 200);
}

export async function hideLoader(force = false) {
  if (dismissed) return;
  const node = el();
  if (!node) { dismissed = true; return; }

  setProgress(1);

  // Hold just long enough that the fill animation completes rather than
  // being cut off mid-rise.
  const elapsed = performance.now() - startedAt;
  const wait = force ? 0 : Math.max(0, MIN_VISIBLE_MS - elapsed);
  if (wait) await new Promise((r) => setTimeout(r, wait));

  dismissed = true;
  clearTimeout(creepTimer);
  try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* private mode */ }

  node.classList.add('is-done');
  node.addEventListener('transitionend', () => node.remove(), { once: true });
  setTimeout(() => node.remove(), 900);   // in case the transition never fires
}

/**
 * Beads on the dark panel above the water line, and bubbles rising inside the
 * liquid. Generated here rather than written into index.html so the inline
 * markup stays small, and so each one gets a randomised size, drift and phase
 * — identical particles on a loop are the tell that something is fake.
 */
export function seedParticles({ beads = 16, bubbles = 18 } = {}) {
  const node = el();
  if (!node || !shouldShowSplash()) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const beadFrag = document.createDocumentFragment();
  for (let i = 0; i < beads; i++) {
    const bead = document.createElement('span');
    const size = 6 + Math.random() * 22;
    bead.className = 'dwl-bead';
    bead.style.cssText =
      `width:${size}px;height:${size}px;left:${Math.random() * 100}%;` +
      `animation-duration:${7 + Math.random() * 8}s;` +
      `animation-delay:${-Math.random() * 9}s;`;
    beadFrag.appendChild(bead);
  }
  node.appendChild(beadFrag);

  const liquid = document.getElementById('dwl-liquid');
  if (!liquid) return;

  const bubbleFrag = document.createDocumentFragment();
  for (let i = 0; i < bubbles; i++) {
    const bubble = document.createElement('span');
    const size = 3 + Math.random() * 11;
    bubble.className = 'dwl-bubble';
    // Bubbles wander sideways as they climb; a dead-straight line looks wrong.
    bubble.style.cssText =
      `width:${size}px;height:${size}px;left:${Math.random() * 100}%;` +
      `--drift:${(Math.random() * 60 - 30).toFixed(0)}px;` +
      `animation-duration:${3.5 + Math.random() * 4}s;` +
      `animation-delay:${-Math.random() * 6}s;`;
    bubbleFrag.appendChild(bubble);
  }
  liquid.appendChild(bubbleFrag);
}
